import api from 'api/api.config';
import type {
  SortDirection,
  FilterValue,
} from 'helpers/api/build-query-params';
import { parsePaginationHeaders } from 'helpers/api/parse-pagination-headers';
import { toQueryParamsObject } from 'helpers/api/to-query-params-object';

import type { CollectionResponse } from 'api/market-view-api';
import {
  getAidModels as getAidModelsOrval,
  createAidModelCopy as createAidModelCopyOrval,
  renameAidModel as renameAidModelOrval,
  updateAidModelArchiveState as updateAidModelArchiveStateOrval,
} from 'api/generated/aid-simulation-models/aid-simulation-models';
import type { AidModelDto } from 'api/generated/schemas/aidModelDto';
import type { AidModelCopyRequest } from 'api/generated/schemas/aidModelCopyRequest';
import type { AidModelArchiveStateRequest } from 'api/generated/schemas/aidModelArchiveStateRequest';
import type { AidModelRenameRequest } from 'api/generated/schemas/aidModelRenameRequest';
import type { GetAidModelsParams } from 'api/generated/schemas/getAidModelsParams';

const getBasePath = (userId: string, clientId: string | number): string =>
  `/${userId}/clients/${clientId}/aid-models`;

export interface AidModelsListParams {
  pageSize?: number;
  pageIndex?: number;
  sortBy?: SortDirection[];
  filters?: Record<string, FilterValue> | null;
}

export const getAidModels = async (
  userId: string,
  clientId: string | number,
  params: AidModelsListParams = {}
): Promise<CollectionResponse<AidModelDto[]>> => {
  const { pageSize, pageIndex = 0, sortBy, filters } = params;

  const response = await getAidModelsOrval(
    userId,
    Number(clientId),
    toQueryParamsObject({
      pageSize,
      pageIndex,
      sortBy,
      filters,
    }) as GetAidModelsParams
  );

  return {
    data: response.data,
    pagination: parsePaginationHeaders(response.headers, {
      pageIndex,
      pageSize,
    }),
  };
};

export const getAidModel = async (
  userId: string,
  clientId: string | number,
  modelId: string | number
): Promise<unknown> => {
  const response = await api.get(`${getBasePath(userId, clientId)}/${modelId}`);

  return response.data;
};

export const createAidModel = async (
  userId: string,
  clientId: string | number,
  data: unknown
): Promise<unknown> => {
  const response = await api.post(
    getBasePath(userId, clientId),
    JSON.stringify(data)
  );

  return response.data;
};

export const updateAidModel = async (
  userId: string,
  clientId: string | number,
  modelId: string | number,
  data: unknown
): Promise<unknown> => {
  const response = await api.put(
    `${getBasePath(userId, clientId)}/${modelId}`,
    JSON.stringify(data)
  );

  return response.data;
};

export const getAidModelDataset = async (
  userId: string,
  modelId: string | number
): Promise<unknown> => {
  const response = await api.get(`/${userId}/aid-models/${modelId}/dataset`);

  return response.data;
};

export const createAidModelDataset = async (
  userId: string,
  modelId: string | number,
  sourceTableName: string
): Promise<unknown> => {
  const response = await api.post(
    `/${userId}/aid-models/${modelId}/dataset`,
    JSON.stringify({ sourceTableName })
  );

  return response.data;
};

export const getAidSourceTables = async (userId: string): Promise<unknown> => {
  const response = await api.get(`/${userId}/aid-source-tables`);

  return response.data;
};

export const createAidModelCopy = (
  userId: string,
  clientId: string | number,
  sourceModelId: string | number,
  data: AidModelCopyRequest
): Promise<AidModelDto> =>
  createAidModelCopyOrval(
    userId,
    Number(clientId),
    Number(sourceModelId),
    data
  );

export const renameAidModel = (
  userId: string,
  clientId: string | number,
  modelId: string | number,
  data: AidModelRenameRequest
): Promise<AidModelDto> =>
  renameAidModelOrval(userId, Number(clientId), Number(modelId), data);

export const updateAidModelArchiveState = (
  userId: string,
  clientId: string | number,
  modelId: string | number,
  data: AidModelArchiveStateRequest
): Promise<AidModelDto> =>
  updateAidModelArchiveStateOrval(
    userId,
    Number(clientId),
    Number(modelId),
    data
  );
